import { useAppState, actions } from "../contexts/AppStateContext";
import { View, StyleSheet, Alert } from "react-native";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useState } from "react";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useNavigation } from "@react-navigation/native";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import {
  GetAllCoursesBySchoolDocument,
  useDeleteCourseCascadeMutation,
  useGetAllCoursesBySchoolQuery,
} from "../graphql/generated/graphql";
import ErrorLoader from "../components/common/status/ErrorLoader/ErrorLoader";
import Loader from "../components/common/status/Loader/Loader";
import DeleteCourseModal from "../components/content/ContentModalContainers/DeleteCourseModal";
import Sentry from "../utils/sentry";
import CustomCourseCard from "../components/customCoursework/CustomCourseCard";
import usePagination from "../hooks/usePagination";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import TableControlsContainer from "../containers/general/TableControls/TableControlsContainer";
import React from "react";
import Pagination from "../components/common/TableControls/Pagination/Pagination";
import { useAppConfig } from "../AppConfigProvider";

const ITEMS_PER_PAGE = 50;

function CustomCourseworkView() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { dispatch } = useAppState();
  const { isMedium } = useResponsiveScreen();

  const [deleteCourseModalVisible, setDeleteCourseModalVisible] = useState(null);

  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const [deleteCourse] = useDeleteCourseCascadeMutation({
    refetchQueries: [
      {
        query: GetAllCoursesBySchoolDocument,
        variables: {
          schoolId,
        },
      },
    ],
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const {
    loading: coursesLoading,
    data,
    error: coursesError,
  } = useGetAllCoursesBySchoolQuery({
    variables: {
      schoolId,
      page,
      pageSize,
    },
    skip: !schoolId,
  });

  const totalItemsCount = data?.getAllCoursesBySchool?.data.totalCount || 0;

  const courses = data?.getAllCoursesBySchool?.data.courses ?? [];

  const handleCreateCourse = () => {
    navigation.navigate("Custom Coursework Course Form");
  };

  const handleCardPress = (courseName, courseId) => {
    navigation.navigate("Custom Coursework Modules", {
      courseId: courseId,
    });
  };

  const handleDeleteCourseModalOpen = (courseId) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setDeleteCourseModalVisible(courseId);
  };

  const handleDeleteCourseModalClose = () => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setDeleteCourseModalVisible(null);
  };

  const handleDeleteCourse = async () => {
    const courseId = deleteCourseModalVisible;

    try {
      const response = await deleteCourse({
        variables: {
          id: courseId,
        },
      });
      if (response.data.deleteCourseCascade.success) {
        Alert.alert("Success", "Module deleted successfully");
        handleDeleteCourseModalClose();
      } else {
        Sentry.captureException(response.data?.deleteCourseCascade.message || "Failed to delete module");
        Alert.alert("Error", response.data?.deleteCourseCascade.message || "Failed to delete module");
      }
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Error", "An error occurred while deleting the module.");
    }
  };

  const handleEditCourse = (courseId) => {
    navigation.navigate("Custom Coursework Course Form", {
      courseId,
    });
  };

  const error = coursesError;

  const loading = coursesLoading;

  if (error) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </DefaultLayout>
    );
  }

  if (loading) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout pageHasChat={false}>
      <View style={styles.headerContainer}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <CustomText
            role="header"
            text="My Courses"
            size={isMedium ? "l" : "xs"}
            textType="display"
            weight="ultraBold"
          />
          <CustomButton
            onPress={handleCreateCourse}
            size="l"
            text={"Create a Course"}
            disabled={loading}
            styleType="primary"
          />
        </View>
      </View>
      <CustomSpacing type="vertical" size="xxl" />
      <CustomSpacing type="vertical" size="xxl" />
      <View>
        {courses.length === 0 ? (
          <View style={styles.emptyCourseContainer}>
            <CustomText
              useTranslationText={false}
              ultraBold
              text="You haven't created a course yet"
              size="xl"
              style={{ color: "white" }}
            />
          </View>
        ) : (
          <>
            {totalItemsCount > ITEMS_PER_PAGE && (
              <Pagination
                page={page}
                handlePageChange={setPage}
                totalPageCount={Math.ceil(totalItemsCount / pageSize)}
              />
            )}
            <CustomSpacing type="vertical" size="m" />
            {courses.map((course, courseIndex) => (
              <CustomCourseCard
                key={course.id}
                course={course}
                courseIndex={courseIndex}
                handleCardPress={handleCardPress}
                handleDeleteCourseModalOpen={handleDeleteCourseModalOpen}
                handleEditCourse={handleEditCourse}
              />
            ))}
            <CustomSpacing type="vertical" size="m" />
            {totalItemsCount > ITEMS_PER_PAGE && (
              <TableControlsContainer
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalCount={totalItemsCount}
              />
            )}
          </>
        )}
      </View>
      <DeleteCourseModal
        visible={!!deleteCourseModalVisible}
        onClose={handleDeleteCourseModalClose}
        onDeleteCourse={handleDeleteCourse}
      />
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  emptyCourseContainer: {
    backgroundColor: "#d3d3d3",
    borderRadius: 20,
    padding: 20,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 30,
  },
  headerContainer: {
    flexDirection: "column",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default CustomCourseworkView;
